import CompanyLookup from '../../components/CompanyLookup';
import { useContext, useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Box, Button, Card, Sheet, Stack, Typography,
  FormControl, FormLabel, FormHelperText, Input, Select, Option,
  Snackbar, Divider, Chip,
} from "@mui/joy";

import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon from "@mui/icons-material/EmailRounded";
import CakeRoundedIcon from "@mui/icons-material/CakeRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import { LayoutContext } from "../../context";
import { APIClientUpdateProfile } from "../../api";
import { splitFullName, joinFullName } from "../../shared/nameUtils";
import MaskedInput from "../../components/JoyMaskedInput";


const CLIENT_CATEGORIES = [
  { value: "network_retail",      label: "Сетевой ритейл" },
  { value: "franchisee",          label: "Франчайзи" },
  { value: "management_company",  label: "Управляющая компания" },
  { value: "designer_project",    label: "Проектировщик" },
  { value: "interior_designer",   label: "Дизайнер интерьера" },
  { value: "design_studio",       label: "Студия дизайна интерьера" },
  { value: "individual_person",   label: "Частное лицо" },
];


export default function ProfilePage() {
  const { account, setAccount } = useContext(LayoutContext);

  // Источник правды для формы — текущий account из контекста
  const buildForm = (acc) => {
    const split = splitFullName(acc?.full_name);
    return {
      last_name:  acc?.last_name  ?? split.last_name,
      name:       acc?.name       ?? split.name,
      patronymic: acc?.patronymic ?? split.patronymic,
      phone:        acc?.phone        || "",
      email:        acc?.email        || "",
      birth_date:   acc?.birth_date   || "",
      client_type:  (acc?.client_type === "legal" ? "ooo" : (acc?.client_type || "individual")),
      client_category: acc?.client_category || "",
      company_name: acc?.company_name || "",
      inn:          acc?.inn          || "",
      kpp:          acc?.kpp          || "",
      ogrn:         acc?.ogrn         || "",
      legal_address:    acc?.legal_address    || "",
      postal_address:   acc?.postal_address   || "",
      director_name:     acc?.director_name     || "",
      director_position: acc?.director_position || "",
      signatory_basis:   acc?.signatory_basis   || "",
      bank_name:        acc?.bank_name         || "",
      bank_bik:         acc?.bank_bik          || "",
      bank_account:     acc?.bank_account      || "",
      bank_corr_account: acc?.bank_corr_account || "",
    };
  };

  const [form, setForm] = useState(() => buildForm(account));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", color: "success" });

  useEffect(() => { setForm(buildForm(account)); }, [account]);

  const set = (k, v) => {
    setForm(p => ({ ...p, [k]: v }));
    setErrors(p => ({ ...p, [k]: "" }));
  };

  const validate = () => {
    const e = {};
    if (!form.last_name.trim()) e.last_name = "Укажите фамилию";
    if (!form.name.trim())      e.name      = "Укажите имя";
    if (!form.phone.replace(/\D/g, "").trim()) e.phone = "Укажите телефон";
    if (!form.email.trim())   e.email   = "Укажите email";
    if (!form.client_type)    e.client_type = "Выберите тип клиента";
    if (!form.client_category) e.client_category = "Выберите категорию";
    // ИНН обязателен для всех, кроме физлица; название компании — для ООО.
    if (["self_employed", "ip", "ip_npd", "ooo"].includes(form.client_type) && !form.inn.trim()) {
      e.inn = "Укажите ИНН";
    }
    if (form.client_type === "ooo" && !form.company_name.trim()) {
      e.company_name = "Укажите название компании";
    }
    return e;
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length) {
      setErrors(e);
      return;
    }
    setSaving(true);
    const payload = {
      last_name:  form.last_name.trim(),
      name:       form.name.trim(),
      patronymic: form.patronymic.trim(),
      phone: form.phone.trim(),
      email: form.email.trim(),
      birth_date: form.birth_date || null,
      client_type: form.client_type,
      client_category: form.client_category,
      // Реквизиты — бэкенд сам очистит неприменимые поля по типу (normalize_by_type).
      company_name: form.company_name.trim() || null,
      inn:          form.inn.trim() || null,
      kpp:          form.kpp.trim() || null,
      ogrn:         form.ogrn.trim() || null,
      legal_address:    form.legal_address.trim() || null,
      postal_address:   form.postal_address.trim() || null,
      director_name:     form.director_name.trim() || null,
      director_position: form.director_position.trim() || null,
      signatory_basis:   form.signatory_basis.trim() || null,
      bank_name:        form.bank_name.trim() || null,
      bank_bik:         form.bank_bik.trim() || null,
      bank_account:     form.bank_account.trim() || null,
      bank_corr_account: form.bank_corr_account.trim() || null,
    };
    const res = await APIClientUpdateProfile(payload);
    setSaving(false);
    if (res?.id) {
      setAccount(res);
      setSnack({ open: true, msg: "Профиль обновлён ✓", color: "success" });
    } else {
      const detail = res?.detail;
      if (detail && typeof detail === "object") {
        setErrors(detail);
        setSnack({ open: true, msg: "Проверьте поля формы", color: "warning" });
      } else {
        setSnack({ open: true, msg: typeof detail === "string" ? detail : "Ошибка", color: "danger" });
      }
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, py: 3, pb: 8 }}>
      <motion.div
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Typography level="h2" fontWeight={800}>Профиль</Typography>
          <CheckCircleRoundedIcon sx={{ fontSize: 28, color: "primary.500" }} />
        </Stack>

        <Card variant="outlined" sx={{ borderRadius: "20px", p: { xs: 2, sm: 3 }, maxWidth: 840, boxSizing: "border-box", boxShadow: "0 4px 24px rgba(30,25,60,0.04)" }}>
          <Stack spacing={2.5}>

            {/* ── Тип клиента ── */}
            <Box>
              <Typography level="body-xs" fontWeight={700} sx={{ mb: 1, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                Тип клиента
              </Typography>
              <Sheet variant="soft" color="neutral" sx={{ borderRadius: "14px", p: 0.75, display: "grid", gap: 0.75, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(5, minmax(0, 1fr))" } }}>
                {[
                  { key: "individual",    label: "Физлицо" },
                  { key: "self_employed", label: "Самозанятый" },
                  { key: "ip",            label: "ИП" },
                  { key: "ip_npd",        label: "ИП на НПД" },
                  { key: "ooo",           label: "ООО" },
                ].map(t => (
                  <Button
                    key={t.key} size="sm"
                    variant={form.client_type === t.key ? "solid" : "plain"}
                    color={form.client_type === t.key ? "primary" : "neutral"}
                    onClick={() => set("client_type", t.key)}
                    aria-pressed={form.client_type === t.key}
                    sx={{ gridColumn: { xs: t.key === "ooo" ? "1 / -1" : "auto", sm: "auto" }, minWidth: 0, minHeight: 42, px: 1, fontWeight: form.client_type === t.key ? 700 : 500, borderRadius: "10px" }}
                  >
                    {t.label}
                  </Button>
                ))}
              </Sheet>
            </Box>

            <FormControl error={!!errors.client_category}>
              <FormLabel>Категория</FormLabel>
              <Select
                placeholder="Выберите категорию"
                value={form.client_category}
                onChange={(_, val) => set("client_category", val)}
              >
                {CLIENT_CATEGORIES.map(c => (
                  <Option key={c.value} value={c.value}>{c.label}</Option>
                ))}
              </Select>
              {errors.client_category && <FormHelperText>{errors.client_category}</FormHelperText>}
            </FormControl>

            <Divider />

            {/* ── ФИО ── */}
            <Typography level="body-xs" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "primary.500" }}>
              <PersonRoundedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
              ФИО
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <FormControl error={!!errors.last_name}>
                <FormLabel>Фамилия <Typography component="span" color="danger">*</Typography></FormLabel>
                <Input value={form.last_name} onChange={e => set("last_name", e.target.value)} placeholder="Иванов" />
                {errors.last_name && <FormHelperText>{errors.last_name}</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.name}>
                <FormLabel>Имя <Typography component="span" color="danger">*</Typography></FormLabel>
                <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Алексей" />
                {errors.name && <FormHelperText>{errors.name}</FormHelperText>}
              </FormControl>
            </Box>

            <FormControl error={!!errors.patronymic} sx={{ maxWidth: { sm: 385 } }}>
              <FormLabel>
                Отчество <Typography component="span" level="body-xs" color="neutral">(необязательно)</Typography>
              </FormLabel>
              <Input value={form.patronymic} onChange={e => set("patronymic", e.target.value)} placeholder="Сергеевич" />
            </FormControl>

            <Divider />

            {/* ── Контакты ── */}
            <Typography level="body-xs" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "primary.500" }}>
              Контакты
            </Typography>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
              <FormControl error={!!errors.phone}>
                <FormLabel>
                  <PhoneRoundedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                  Телефон <Typography component="span" color="danger">*</Typography>
                </FormLabel>
                <MaskedInput
                  mask="+7 (000) 000-00-00"
                  placeholder="+7 (___) ___-__-__"
                  value={form.phone}
                  onChange={e => set("phone", e.target.value)}
                />
                {errors.phone && <FormHelperText>{errors.phone}</FormHelperText>}
              </FormControl>
              <FormControl error={!!errors.email}>
                <FormLabel>
                  <EmailRoundedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                  Email <Typography component="span" color="danger">*</Typography>
                </FormLabel>
                <Input
                  type="email"
                  value={form.email}
                  onChange={e => set("email", e.target.value)}
                  placeholder="example@mail.ru"
                />
                {errors.email && <FormHelperText>{errors.email}</FormHelperText>}
              </FormControl>
            </Box>

            <FormControl error={!!errors.birth_date} sx={{ maxWidth: { sm: 260 } }}>
              <FormLabel>
                <CakeRoundedIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: "middle" }} />
                Дата рождения <Typography component="span" level="body-xs" color="neutral">(необязательно)</Typography>
              </FormLabel>
              <Input
                type="date"
                value={form.birth_date}
                onChange={e => set("birth_date", e.target.value)}
                slotProps={{ input: { max: new Date().toISOString().split("T")[0] } }}
              />
              {errors.birth_date && <FormHelperText>{errors.birth_date}</FormHelperText>}
            </FormControl>

            {/* ── Реквизиты (для не-физлица) ── */}
            {form.client_type !== "individual" && (
              <>
                <Divider />
                <Typography level="body-xs" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "primary.500" }}>
                  <BusinessRoundedIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: "middle" }} />
                  Реквизиты
                </Typography>

                {form.client_type === "ooo" && (
                  <FormControl error={!!errors.company_name}>
                    <FormLabel>Название компании <Typography component="span" color="danger">*</Typography></FormLabel>
                    <Input value={form.company_name} onChange={e => set("company_name", e.target.value)} placeholder="ООО «Ромашка»" />
                    {errors.company_name && <FormHelperText>{errors.company_name}</FormHelperText>}
                  </FormControl>
                )}

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FormControl error={!!errors.inn}>
                    <FormLabel>ИНН <Typography component="span" color="danger">*</Typography></FormLabel>
                    <Input value={form.inn} onChange={e => set("inn", e.target.value)} placeholder="7701234567" />
                    {errors.inn && <FormHelperText>{errors.inn}</FormHelperText>}
                  </FormControl>
                  {form.client_type === "ooo" && (
                    <FormControl>
                      <FormLabel>КПП</FormLabel>
                      <Input value={form.kpp} onChange={e => set("kpp", e.target.value)} placeholder="770101001" />
                    </FormControl>
                  )}
                  <FormControl>
                    <FormLabel>ОГРН/ОГРНИП</FormLabel>
                    <Input value={form.ogrn} onChange={e => set("ogrn", e.target.value)} placeholder="" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Юридический адрес</FormLabel>
                    <Input value={form.legal_address} onChange={e => set("legal_address", e.target.value)} placeholder="" />
                  </FormControl>
                </Box>

                <CompanyLookup form={form} setForm={setForm} token={localStorage.getItem('client_token') || ''} ownerKey={`profile:${account?.id || ''}`} disabled={saving} />
                {form.client_type === "ooo" && (
                  <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                    <FormControl>
                      <FormLabel>Руководитель (ФИО)</FormLabel>
                      <Input value={form.director_name} onChange={e => set("director_name", e.target.value)} placeholder="Иванов И. И." />
                    </FormControl>
                    <FormControl>
                      <FormLabel>Должность</FormLabel>
                      <Input value={form.director_position} onChange={e => set("director_position", e.target.value)} placeholder="Генеральный директор" />
                    </FormControl>
                  </Box>
                )}

                <Typography level="body-xs" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: "0.06em", color: "primary.500", mt: 0.5 }}>
                  Банковские реквизиты
                </Typography>
                <FormControl>
                  <FormLabel>Банк</FormLabel>
                  <Input value={form.bank_name} onChange={e => set("bank_name", e.target.value)} placeholder="ПАО Сбербанк" />
                </FormControl>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <FormControl>
                    <FormLabel>БИК</FormLabel>
                    <Input value={form.bank_bik} onChange={e => set("bank_bik", e.target.value)} placeholder="044525225" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Расчётный счёт</FormLabel>
                    <Input value={form.bank_account} onChange={e => set("bank_account", e.target.value)} placeholder="40702810…" />
                  </FormControl>
                  <FormControl>
                    <FormLabel>Корр. счёт</FormLabel>
                    <Input value={form.bank_corr_account} onChange={e => set("bank_corr_account", e.target.value)} placeholder="30101810…" />
                  </FormControl>
                </Box>
              </>
            )}

            <Divider />

            <Button
              size="lg" loading={saving}
              startDecorator={<SaveRoundedIcon />}
              onClick={handleSave}
              sx={{
                borderRadius: "14px", fontWeight: 700, alignSelf: { xs: "stretch", sm: "flex-start" }, px: 3,
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                boxShadow: "0 4px 16px rgba(99,102,241,0.35)",
              }}
            >
              Сохранить изменения
            </Button>

          </Stack>
        </Card>
      </motion.div>

      <Snackbar
        open={snack.open}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        autoHideDuration={3500}
        color={snack.color}
        variant="soft"
      >
        {snack.msg}
      </Snackbar>
    </Box>
  );
}